import { Card, CardContent, CardHeader, CardTitle } from '@xhubio-saas/core-frontend/ui-components'
import { formatDate as formatLocaleDate, useLocale } from '@xhubio-saas/country-specifics-frontend'
import { Layers2Icon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useVersionInfo } from '../../../hooks/useVersionInfo'
import { SettingsLayout } from '../../../layouts/SettingsLayout'

/**
 * Version info row component
 */
function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between py-2 border-b border-border last:border-0">
      <span className="text-muted-foreground text-sm">{label}</span>
      <span className="font-mono text-sm">{value}</span>
    </div>
  )
}

/**
 * Settings system page - displays version information
 *
 * Shows frontend and backend version, build dates, and commit SHAs.
 * The release changelog lives on its own page (`/changelog`, main sidebar).
 * Useful for support, debugging, and transparency.
 */
export function SettingsSystemPage() {
  const { t } = useTranslation()
  const locale = useLocale()
  const formatDate = (isoString: string): string => formatLocaleDate(isoString, locale)
  const { frontend, backend, isLoading, error } = useVersionInfo()

  return (
    <SettingsLayout section="system">
      <div
        className="flex flex-col gap-4 items-stretch sm:flex-row"
        data-testid="settings-system-page"
      >
        {/* Frontend Version Card */}
        <Card className="w-full max-w-sm flex flex-col">
          <CardHeader>
            <div className="flex gap-4">
              <Layers2Icon className="text-muted-foreground" />
              <div className="space-y-2">
                <CardTitle>{t('settings.system.frontendTitle', 'Frontend')}</CardTitle>
              </div>
            </div>
          </CardHeader>
          <CardContent className="flex-1">
            <InfoRow label={t('settings.system.version', 'Version')} value={frontend.version} />
            <InfoRow
              label={t('settings.system.buildDate', 'Build Date')}
              value={formatDate(frontend.buildDate)}
            />
            <InfoRow label={t('settings.system.commitSha', 'Commit')} value={frontend.commitSha} />
          </CardContent>
        </Card>

        {/* Backend Version Card */}
        <Card className="w-full max-w-sm flex flex-col">
          <CardHeader>
            <div className="flex gap-4">
              <Layers2Icon className="text-muted-foreground" />
              <div className="space-y-2">
                <CardTitle>{t('settings.system.backendTitle', 'Backend')}</CardTitle>
              </div>
            </div>
          </CardHeader>
          <CardContent className="flex-1">
            {isLoading ? (
              <div className="text-muted-foreground ">{t('common.loading', 'Loading...')}</div>
            ) : error ? (
              <div className="text-destructive ">
                {t('settings.system.loadError', 'Failed to load backend version')}
              </div>
            ) : backend ? (
              <>
                <InfoRow label={t('settings.system.version', 'Version')} value={backend.version} />
                <InfoRow
                  label={t('settings.system.buildDate', 'Build Date')}
                  value={formatDate(backend.buildDate)}
                />
                <InfoRow
                  label={t('settings.system.commitSha', 'Commit')}
                  value={backend.commitSha}
                />
              </>
            ) : (
              <div className="text-muted-foreground ">
                {t('settings.system.unavailable', 'Version information unavailable')}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </SettingsLayout>
  )
}
